import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { ArrowRight } from 'lucide-react';
import { getCompanyInfo } from '../data/api';

const HeroProfessional = () => {
  const [scrollY, setScrollY] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [companyInfo, setCompanyInfo] = useState({ name: '', tagline: '', description: '', projectsCompleted: '', happyClients: '' });

  useEffect(() => {
    getCompanyInfo().then(setCompanyInfo).catch(() => {});
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20
      });
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative overflow-hidden">
      {/* Main Hero Section */}
      <div className="relative h-screen bg-white">
        {/* Plain White Background */}
        <div className="absolute inset-0">
          {/* Subtle decorative elements */}
          <div className="absolute inset-0 bg-gradient-to-br from-white via-gray-50/50 to-blue-50/30" />
          
          {/* Floating Particles */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="particle particle-1"></div>
            <div className="particle particle-2"></div>
            <div className="particle particle-3"></div>
            <div className="particle particle-4"></div>
            <div className="particle particle-5"></div>
          </div>
        </div>

        {/* Content Container with Fade-in Animation */}
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-5xl px-4 sm:px-6 lg:px-8 lg:ml-20">
            <div className="space-y-8">
              {/* SEO-Optimized H1 with Keywords */}
              <div className="space-y-4">
                {/* Hidden SEO H1 for search engines */}
                <h1 className="sr-only">Interior Design and Renovation Company Singapore - Yusen Design & Contract</h1>
                
                {/* Visible animated heading */}
                <div className="text-4xl md:text-5xl lg:text-7xl font-light text-gray-900 leading-tight tracking-tight" role="heading" aria-level="2">
                  <span 
                    className="inline-block animate-slideInLeft opacity-0"
                    style={{ animationDelay: '0.2s', animationFillMode: 'forwards' }}
                  >
                    Transforming
                  </span>
                  <span 
                    className="block font-semibold text-blue-600 animate-slideInLeft opacity-0 hover:text-blue-500 transition-colors duration-300"
                    style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}
                  >
                    Spaces
                  </span>
                  <span 
                    className="block text-3xl md:text-4xl lg:text-5xl text-gray-600 mt-2 animate-slideInLeft opacity-0"
                    style={{ animationDelay: '0.6s', animationFillMode: 'forwards' }}
                  >
                    Crafting Dreams
                  </span>
                </div>
                
                {/* SEO subtitle */}
                <p 
                  className="text-sm text-blue-600 font-medium tracking-wide animate-fadeInUp opacity-0"
                  style={{ animationDelay: '0.7s', animationFillMode: 'forwards' }}
                >
                  #1 Interior Design & Renovation Company in Singapore
                </p>
              </div>

              {/* Description with Fade Animation */}
              <p 
                className="text-base sm:text-lg md:text-xl text-gray-600 leading-relaxed max-w-2xl animate-fadeInUp opacity-0"
                style={{ animationDelay: '0.8s', animationFillMode: 'forwards' }}
              >
                {companyInfo.description}
              </p>

              {/* CTA Buttons with Hover Effects */}
              <div 
                className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4 animate-fadeInUp opacity-0"
                style={{ animationDelay: '1s', animationFillMode: 'forwards' }}
              >
                <Button
                  onClick={() => scrollToSection('portfolio')}
                  size="lg"
                  className="group bg-blue-600 hover:bg-blue-500 text-white px-6 sm:px-10 py-4 sm:py-6 text-base sm:text-lg font-medium rounded-none shadow-lg hover:shadow-blue-500/30 hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 hover:scale-105"
                >
                  View Our Work
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-2 transition-transform duration-300" />
                </Button>
                <Button
                  onClick={() => scrollToSection('contact')}
                  size="lg"
                  variant="outline"
                  className="border-2 border-gray-300 hover:border-blue-600 text-gray-700 hover:text-blue-600 hover:bg-blue-50/50 px-6 sm:px-10 py-4 sm:py-6 text-base sm:text-lg font-medium rounded-none transition-all duration-500 hover:-translate-y-1 hover:scale-105 hover:shadow-lg"
                >
                  Get In Touch
                </Button>
              </div>

              {/* Stats Row with Counter Animation */}
              <div 
                className="flex gap-6 sm:gap-10 md:gap-16 pt-6 sm:pt-10 border-t border-gray-200 animate-fadeInUp opacity-0"
                style={{ animationDelay: '1.2s', animationFillMode: 'forwards' }}
              >
                <div className="group cursor-pointer">
                  <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors duration-300 group-hover:scale-110 transform origin-left">
                    {companyInfo.projectsCompleted}
                  </div>
                  <div className="text-sm text-gray-500 uppercase tracking-wider mt-1 group-hover:text-gray-700 transition-colors duration-300">Projects</div>
                </div>
                <div className="group cursor-pointer">
                  <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors duration-300 group-hover:scale-110 transform origin-left">
                    {companyInfo.happyClients}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-500 uppercase tracking-wider mt-1 group-hover:text-gray-700 transition-colors duration-300">Clients</div>
                </div>
                <div className="group cursor-pointer">
                  <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors duration-300 group-hover:scale-110 transform origin-left">38+</div>
                  <div className="text-xs sm:text-sm text-gray-500 uppercase tracking-wider mt-1 group-hover:text-gray-700 transition-colors duration-300">Years</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Animated Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-gray-400 z-10 animate-bounce-slow hidden sm:flex">
          <span className="text-xs uppercase tracking-widest">Scroll</span>
          <div className="w-6 h-10 border-2 border-gray-300 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-gray-400 rounded-full animate-scroll-down"></div>
          </div>
        </div>
        
        {/* Decorative Animated Lines */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/50 to-transparent animate-pulse"></div>
      </div>

      {/* Featured Projects Section */}
      <div className="bg-gray-50 py-12 sm:py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4 mb-8 sm:mb-12">
            <div>
              <p className="text-blue-600 font-medium mb-2 uppercase tracking-wider text-sm">Portfolio</p>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900">Featured Projects</h2>
            </div>
            <Button
              onClick={() => scrollToSection('portfolio')}
              variant="ghost"
              className="text-blue-600 hover:text-blue-700 hidden md:flex"
            >
              View All Projects
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>

          {/* Projects Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
            {/* Project 1 */}
            <div className="group relative aspect-[4/5] sm:aspect-[4/5] overflow-hidden bg-gray-200">
              <img
                src="https://customer-assets.emergentagent.com/job_36f0fb6a-8309-4c33-95e8-187581953690/artifacts/5d3ecqia_WhatsApp%20Image%202026-01-05%20at%2011.43.47%20PM%20%283%29_Easy-Resize.com.jpg"
                alt="Tarboush Restaurant"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
                <p className="text-blue-400 text-sm font-medium mb-1">Hospitality</p>
                <h3 className="text-white text-lg sm:text-xl font-semibold">Tarboush Restaurant</h3>
                <p className="text-gray-300 text-sm mt-1">Orchard Rendezvous Hotel</p>
              </div>
            </div>

            {/* Project 2 */}
            <div className="group relative aspect-[4/5] sm:aspect-[4/5] overflow-hidden bg-gray-200">
              <img
                src="https://customer-assets.emergentagent.com/job_36f0fb6a-8309-4c33-95e8-187581953690/artifacts/uk0figs4_WhatsApp%20Image%202026-01-05%20at%2011.35.44%20PM%20%282%29_Easy-Resize.com%20%281%29.jpg"
                alt="Munchi Pancakes"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
                <p className="text-blue-400 text-sm font-medium mb-1">Signage</p>
                <h3 className="text-white text-lg sm:text-xl font-semibold">Munchi Pancakes</h3>
                <p className="text-gray-300 text-sm mt-1">Commercial Signage</p>
              </div>
            </div>

            {/* Project 3 */}
            <div className="group relative aspect-[4/5] sm:aspect-[4/5] overflow-hidden bg-gray-200 sm:col-span-2 md:col-span-1">
              <img
                src="https://customer-assets.emergentagent.com/job_36f0fb6a-8309-4c33-95e8-187581953690/artifacts/0p1ebr08_WhatsApp%20Image%202026-01-05%20at%2011.35.44%20PM_Easy-Resize.com.jpg"
                alt="Hakka Bond"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
                <p className="text-blue-400 text-sm font-medium mb-1">Hospitality</p>
                <h3 className="text-white text-lg sm:text-xl font-semibold">Hakka Bond</h3>
                <p className="text-gray-300 text-sm mt-1">Restaurant Design</p>
              </div>
            </div>
          </div>

          {/* Mobile View All Button */}
          <div className="mt-8 text-center md:hidden">
            <Button
              onClick={() => scrollToSection('portfolio')}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              View All Projects
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Certifications Bar */}
      <div className="bg-white py-12 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center items-center gap-10 md:gap-16">
            <img
              src="https://customer-assets.emergentagent.com/job_yusenspaces/artifacts/h4dzrmvg_share-default.png"
              alt="BCA Licensed"
              className="h-16 w-auto object-contain grayscale hover:grayscale-0 opacity-60 hover:opacity-100 transition-all duration-300"
            />
            <img
              src="https://customer-assets.emergentagent.com/job_yusenspaces/artifacts/w2ad4u3e_45001-1.png"
              alt="ISO 45001:2018"
              className="h-16 w-auto object-contain grayscale hover:grayscale-0 opacity-60 hover:opacity-100 transition-all duration-300"
            />
            <img
              src="https://customer-assets.emergentagent.com/job_yusenspaces/artifacts/hvr1oriq_SS506-SAC-400x400.png"
              alt="EQAIMS Accredited"
              className="h-16 w-auto object-contain grayscale hover:grayscale-0 opacity-60 hover:opacity-100 transition-all duration-300"
              style={{ mixBlendMode: 'multiply' }}
            />
            <img
              src="https://customer-assets.emergentagent.com/job_yusenspaces/artifacts/97ulnv35_BIZSAFE.png"
              alt="bizSAFE Star"
              className="h-16 w-auto object-contain grayscale hover:grayscale-0 opacity-60 hover:opacity-100 transition-all duration-300"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroProfessional;
